import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Copy, Check, Clock, Link2, Monitor, Building2, Network,
  ChevronLeft, ChevronRight, Sparkles, ShieldAlert, Cpu, ExternalLink,
} from 'lucide-react';
import StatusSelect from '../components/StatusSelect';
import AssetHoverLabel from '../components/AssetHoverLabel';
import IpReputationBadge from '../components/IpReputationBadge';
import CustomerIpBadge from '../components/CustomerIpBadge';
import { CustomerIpBlock, matchCustomerIp } from '../utils/customerIps';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import EdrResponsePanel from '../components/EdrResponsePanel';

interface AlertDetail {
  id: number;
  host: string;
  message: string;
  raw_event: string | null;
  source: string | null;
  created_at: string;
  dedup_count: number;
  last_seen_at: string | null;
  last_reported_at: string | null;
  rule_name: string | null;
  rule_keyword: string | null;
  rule_mitre_tactic: string | null;
  rule_mitre_technique: string | null;
  correlation_window_minutes: number | null;
  case_id: number;
  case_title: string;
  case_severity: 'low' | 'medium' | 'high' | 'critical';
  case_status: 'new' | 'investigating' | 'contained' | 'closed';
  case_mitre_tactic: string | null;
  case_mitre_technique: string | null;
  case_alert_count: number;
  tenant_name: string;
  asset_id: number | null;
  asset_ip_address: string | null;
  asset_mac_address: string | null;
  // Link all'oggetto nella console del vendor, già verificato dal backend (solo host Acronis).
  vendor_link?: string | null;
}

interface ParsedLog {
  host: string;
  message: string;
  facility: number | null;
  severity: number | null;
  format: string;
  fields?: Record<string, string>;
  normalized?: {
    srcIp?: string; srcPort?: string; dstIp?: string; dstPort?: string;
    protocol?: string; action?: string; user?: string; url?: string;
    threat?: string; ruleName?: string; vendor?: string; product?: string;
  };
  cef?: {
    deviceVendor: string;
    deviceProduct: string;
    deviceVersion: string;
    signatureId: string;
    name: string;
    severity: string;
    extensions: Record<string, string>;
  };
}

interface Occurrence {
  id: number;
  message: string;
  raw_event: string | null;
  created_at: string;
  parsed: ParsedLog | null;
}

const SEVERITY_BAR: Record<string, string> = {
  low: 'bg-gray-400', medium: 'bg-amber-500', high: 'bg-orange-500', critical: 'bg-red-600',
};

const TABS = ['Details', 'Alert Fields', 'Raw Log'] as const;
type Tab = typeof TABS[number];

// Etichette leggibili per le estensioni CEF standard più comuni.
const CEF_LABELS: Record<string, string> = {
  src: 'Source IP', spt: 'Source port', shost: 'Source host', smac: 'Source MAC',
  dst: 'Destination IP', dpt: 'Destination port', dhost: 'Destination host', dmac: 'Destination MAC',
  proto: 'Protocol', act: 'Action', app: 'Application', dvc: 'Device IP', dvchost: 'Device host',
  dvcmac: 'Device MAC', suser: 'Source user', duser: 'Destination user', cs1: 'Custom field 1',
  msg: 'Message', outcome: 'Outcome', request: 'Request', fileHash: 'File hash', filePath: 'File path',
};

function isPrivateIp(ip: string): boolean {
  return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|127\.)/.test(ip);
}

function FieldsTable({ fields }: { fields: Record<string, string> }) {
  const FIELDS_COLUMNS: ColumnDef[] = [
    { key: 'field', label: 'Field', defaultVisible: true },
    { key: 'name', label: 'Name', defaultVisible: true },
    { key: 'value', label: 'Value', defaultVisible: true },
  ];
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.alert_fields', FIELDS_COLUMNS);
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          {orderedDefs.map(d => (
            <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
              {d.label}
            </DraggableTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {Object.entries(fields).map(([k, v]) => (
          <tr key={k} className="border-t border-gray-100">
            {orderedDefs.map(d => (
              <td key={d.key} className={`table-cell ${d.key === 'field' ? 'font-mono text-xs text-gray-500' : d.key === 'name' ? 'text-gray-600' : 'text-gray-900'}`}>
                {d.key === 'field' && k}
                {d.key === 'name' && (CEF_LABELS[k] || '—')}
                {d.key === 'value' && v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AlertDetailPage() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<AlertDetail | null>(null);
  const [parsed, setParsed] = useState<ParsedLog | null>(null);
  const [customerIps, setCustomerIps] = useState<CustomerIpBlock[]>([]);
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [previousId, setPreviousId] = useState<number | null>(null);
  const [nextId, setNextId] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>('Details');
  const [copied, setCopied] = useState(false);

  async function load() {
    const res = await axios.get(`/api/alerts/${alertId}`);
    setAlert(res.data.data.alert);
    setCustomerIps(res.data.data.customerIps || []);
    setParsed(res.data.data.parsed);
    setOccurrences(res.data.data.occurrences || []);
    setPreviousId(res.data.data.previousId);
    setNextId(res.data.data.nextId);
  }

  useEffect(() => { load(); }, [alertId]);

  if (!alert) return <div className="p-4 text-gray-400">Loading…</div>;

  const ext = parsed?.fields || {};
  const norm = parsed?.normalized || {};
  const srcIp = norm.srcIp;
  const dstIp = norm.dstIp;

  async function copyId() {
    await navigator.clipboard.writeText(`alert-${alert!.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function changeStatus(status: string) {
    await axios.put(`/api/cases/${alert!.case_id}`, { status });
    load();
  }

  return (
    <div>
      <Link to="/alerts" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3">
        <ArrowLeft size={16} /> Back to Security Alerts
      </Link>

      <div className="card overflow-hidden">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`w-1.5 h-6 rounded-sm ${SEVERITY_BAR[alert.case_severity]}`} />
            <h1 className="text-lg font-semibold text-gray-900">{alert.rule_name || 'Unclassified alert'}</h1>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              ID:alert-{alert.id}
              <button onClick={copyId} className="hover:text-gray-700" title="Copy alert ID">
                {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
              </button>
            </span>
            <StatusSelect value={alert.case_status} onChange={changeStatus} />
            {alert.vendor_link && (
              <a href={alert.vendor_link} target="_blank" rel="noopener noreferrer" className="btn-secondary flex items-center gap-1 text-sm">
                <ExternalLink size={13} /> Open in Acronis console
              </a>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => previousId && navigate(`/alerts/${previousId}`)} disabled={!previousId}
                className="btn-secondary flex items-center gap-1 disabled:opacity-40">
                <ChevronLeft size={14} /> Previous
              </button>
              <button onClick={() => nextId && navigate(`/alerts/${nextId}`)} disabled={!nextId}
                className="btn-secondary flex items-center gap-1 disabled:opacity-40">
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-4 text-sm">
            <div>
              <span className="text-gray-400 text-xs block">Last Detected</span>
              <span className="text-gray-800 flex items-center gap-1">
                <Clock size={13} className="text-gray-400" />
                {new Date(alert.created_at).toLocaleString('en-GB')}
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Associated Incident</span>
              <Link to={`/incidents/${alert.case_id}`} className="text-primary-700 hover:underline flex items-center gap-1">
                <Link2 size={13} /> {alert.case_title}
                <span className="badge bg-gray-100 text-gray-600 ml-1">
                  {alert.case_alert_count} {alert.case_alert_count === 1 ? 'alert' : 'alerts'}
                </span>
              </Link>
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Affected Assets</span>
              <span className="flex items-center gap-3 text-gray-800">
                <span className="flex items-center gap-1"><Monitor size={13} className="text-gray-400" />
                  <AssetHoverLabel hostname={alert.host} ipAddress={alert.asset_ip_address} macAddress={alert.asset_mac_address} assetId={alert.asset_id} />
                </span>
                {alert.asset_ip_address && (
                  <span className="flex items-center gap-1"><Network size={13} className="text-gray-400" />{alert.asset_ip_address}</span>
                )}
                <span className="flex items-center gap-1"><Building2 size={13} className="text-gray-400" />{alert.tenant_name}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-6 px-5 border-b border-gray-100">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                tab === t ? 'border-primary-600 text-primary-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'Details' && (
          <div className="p-5 space-y-5">
            <EdrResponsePanel alertId={alert.id} onDone={load} />
            <div className="rounded-lg bg-gradient-to-r from-emerald-50 to-white border border-emerald-100 px-4 py-2.5 flex items-center gap-2">
              <Sparkles size={15} className="text-emerald-500" />
              <span className="text-sm font-medium text-gray-800">Analysis Overview</span>
              <span className="text-sm text-gray-500">
                {alert.rule_name
                  ? `Rule "${alert.rule_name}" matched on ${alert.host}.` +
                    (alert.case_alert_count > 1
                      ? ` Correlated into incident "${alert.case_title}" with ${alert.case_alert_count - 1} other alert(s) on the same asset.`
                      : ` It is currently the only alert in incident "${alert.case_title}".`)
                  : `Event recorded on ${alert.host}.`}
              </span>
            </div>

            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Basics</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="text-gray-400 w-36 shrink-0">Data Source</dt>
                  <dd className="text-gray-800 flex items-center gap-1">
                    <ShieldAlert size={13} className="text-primary-500" />
                    {parsed?.cef ? `${parsed.cef.deviceVendor} ${parsed.cef.deviceProduct}` : alert.source || 'syslog'}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-gray-400 w-36 shrink-0">Detection Engine</dt>
                  <dd><span className="badge bg-primary-50 text-primary-700 flex items-center gap-1 w-fit">
                    <Cpu size={11} /> Correlation Engine
                  </span></dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-gray-400 w-36 shrink-0">MITRE ATT&CK</dt>
                  <dd className="text-gray-800">
                    {alert.rule_mitre_tactic || alert.case_mitre_tactic || '—'}
                    {(alert.rule_mitre_technique || alert.case_mitre_technique) &&
                      ` (${alert.rule_mitre_technique || alert.case_mitre_technique})`}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-gray-400 w-36 shrink-0">Log Format</dt>
                  <dd className="text-gray-800 uppercase">{parsed?.format || '—'}</dd>
                </div>
                <div className="flex gap-3 md:col-span-2">
                  <dt className="text-gray-400 w-36 shrink-0">Alert Description</dt>
                  <dd className="text-gray-700">{alert.message}</dd>
                </div>
                {alert.rule_keyword && (
                  <div className="flex gap-3 md:col-span-2">
                    <dt className="text-gray-400 w-36 shrink-0">Match Condition</dt>
                    <dd><code className="text-xs bg-gray-100 rounded px-1.5 py-0.5 text-gray-700">message contains "{alert.rule_keyword}"</code></dd>
                  </div>
                )}
              </dl>
            </section>

            {(srcIp || dstIp) && (
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">IP Information</h3>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-5 flex items-center justify-center gap-4 flex-wrap">
                  {srcIp && (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm w-56">
                      <div className="px-3 py-1 text-xs text-primary-600 border-b border-gray-100">• Src IP</div>
                      <div className="px-3 py-2">
                        {matchCustomerIp(srcIp, customerIps)
                          ? <div className="mb-1"><CustomerIpBadge label={matchCustomerIp(srcIp, customerIps)!.label} siteName={matchCustomerIp(srcIp, customerIps)!.siteName} /></div>
                          : <div className="badge bg-blue-50 text-blue-700 mb-1">{isPrivateIp(srcIp) ? 'Internal IP' : 'Public IP'}</div>}
                        <div className="font-medium text-gray-900">{srcIp}</div>
                        {norm.srcPort && <div className="text-xs text-gray-500 mt-1">Port: {norm.srcPort}</div>}
                        {norm.user && <div className="text-xs text-gray-500">User: {norm.user}</div>}
                        {!isPrivateIp(srcIp) && <IpReputationBadge ip={srcIp} />}
                      </div>
                    </div>
                  )}
                  {srcIp && dstIp && (
                    <div className="flex flex-col items-center text-xs text-gray-500 min-w-[120px]">
                      <span>{norm.action || 'Observed'}</span>
                      <div className="w-full h-px bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200 my-1" />
                      <span>Protocol: {norm.protocol || 'n/a'}</span>
                    </div>
                  )}
                  {dstIp && (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm w-56">
                      <div className="px-3 py-1 text-xs text-primary-600 border-b border-gray-100">• Dst IP</div>
                      <div className="px-3 py-2">
                        {matchCustomerIp(dstIp, customerIps)
                          ? <div className="mb-1"><CustomerIpBadge label={matchCustomerIp(dstIp, customerIps)!.label} siteName={matchCustomerIp(dstIp, customerIps)!.siteName} /></div>
                          : <div className="badge bg-blue-50 text-blue-700 mb-1">{isPrivateIp(dstIp) ? 'Internal IP' : 'Public IP'}</div>}
                        <div className="font-medium text-gray-900">{dstIp}</div>
                        {norm.dstPort && <div className="text-xs text-gray-500 mt-1">Port: {norm.dstPort}</div>}
                        {!isPrivateIp(dstIp) && <IpReputationBadge ip={dstIp} />}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Forensic Details</h3>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 border border-gray-100 rounded-lg p-4">
                <div>
                  <dt className="text-gray-400 text-xs">Detected at</dt>
                  <dd className="text-gray-800">{new Date(alert.created_at).toLocaleString('en-GB')}</dd>
                </div>
                <div>
                  <dt className="text-gray-400 text-xs">Correlation window</dt>
                  <dd className="text-gray-800">{alert.correlation_window_minutes ?? '—'} min</dd>
                </div>
                <div>
                  <dt className="text-gray-400 text-xs">Affected MAC</dt>
                  <dd className="text-gray-800 font-mono text-xs">{alert.asset_mac_address || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-400 text-xs">Alerts in incident</dt>
                  <dd className="text-gray-800">{alert.case_alert_count}</dd>
                </div>
                <div>
                  <dt className="text-gray-400 text-xs">Occurrences</dt>
                  <dd className="text-gray-800">
                    {alert.dedup_count}
                    {alert.dedup_count > 1 && alert.last_seen_at &&
                      ` (last at ${new Date(alert.last_seen_at).toLocaleString('en-GB')})`}
                  </dd>
                </div>
              </dl>
            </section>
          </div>
        )}

        {tab === 'Alert Fields' && (
          <div className="p-5 space-y-4">
            {occurrences.length > 1 ? (
              occurrences.some(o => o.parsed?.fields && Object.keys(o.parsed.fields).length > 0) ? (
                occurrences.map((o, i) => (
                  <div key={o.id}>
                    <div className="text-xs text-gray-500 mb-1">
                      #{i + 1} — {new Date(o.created_at).toLocaleString('en-GB')}
                    </div>
                    {o.parsed?.fields && Object.keys(o.parsed.fields).length > 0 ? (
                      <FieldsTable fields={o.parsed.fields} />
                    ) : (
                      <p className="text-sm text-gray-400">No structured fields for this occurrence.</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">No structured fields available: these events are not in CEF format.</p>
              )
            ) : Object.keys(ext).length === 0 ? (
              <p className="text-sm text-gray-400">No structured fields available: this event is not in CEF format.</p>
            ) : (
              <FieldsTable fields={ext} />
            )}
          </div>
        )}

        {tab === 'Raw Log' && (
          <div className="p-5 space-y-3">
            {occurrences.length > 0 ? (
              <>
                {alert.dedup_count > occurrences.length && (
                  <p className="text-xs text-gray-500">
                    Showing the first {occurrences.length} of {alert.dedup_count} grouped occurrences
                    (all events remain available in the log store).
                  </p>
                )}
                {occurrences.map((o, i) => (
                  <div key={o.id}>
                    <div className="text-xs text-gray-500 mb-1">
                      #{i + 1} — {new Date(o.created_at).toLocaleString('en-GB')}
                    </div>
                    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs whitespace-pre-wrap break-all">
                      {o.raw_event || o.message}
                    </pre>
                  </div>
                ))}
              </>
            ) : (
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs whitespace-pre-wrap break-all">
                {alert.raw_event || 'Raw event not available'}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
